import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Tab, TabGroup } from '@base-app';
import { PageLarge } from '@builder';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants/common';
import { Sizes } from '@common/types';
import { TabContextProvider } from '@common/components';
import { TabId } from '@modules/study-insights/types';

import { useFeatureFlags } from '@/modules/feature-flags/hooks';
import FeatureFlags from '@/modules/feature-flags/types/feature-flags.types';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PATHS } from '@/common/constants';
import { TabLink } from '@/common/components/base-app/atoms/Tab/Tab.types';
import { Permissions } from '@modules/permissions/types';
import { usePermissions } from '@modules/permissions/hooks';
import { getTabsList, getTitleAndTooltip } from './StudyInsightsScreen.helper';
import useOrganizationName from '@/modules/my-account/hooks/useOrganizationName';

const StudyInsightsScreen: FC = () => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const { isFeatureEnabled } = useFeatureFlags();
  const { hasPermission } = usePermissions();
  const hasParticipantRecruitmentPartnerLevel =
    hasPermission(Permissions.STUDY_INSIGHTS__RECRUITMENT_LAYOUT_1) ||
    hasPermission(Permissions.STUDY_INSIGHTS__RECRUITMENT_LAYOUT_3);
  const isParticipantEnabled =
    isFeatureEnabled(FeatureFlags.PARTICIPANT_RECRUITMENT) && hasParticipantRecruitmentPartnerLevel;
  const location = useLocation();
  const navigate = useNavigate();
  const organizationName = useOrganizationName();

  const tabsList: TabLink[] = getTabsList(t, isFeatureEnabled, hasPermission);

  const [selectedTab, setSelectedTab] = useState<string>(() => {
    const urlSelectedTab = tabsList.find((tab) => location.pathname.includes(tab.value));
    if (urlSelectedTab) {
      return urlSelectedTab.value;
    }
    return isParticipantEnabled ? TabId.RECRUITMENT : TabId.COLLECTION;
  });

  const titleAndTooltip = getTitleAndTooltip(selectedTab, t, organizationName);

  useEffect(() => {
    const urlSelectedTab = tabsList.find((tab) => location.pathname.includes(tab.value));
    if (urlSelectedTab) {
      setSelectedTab(urlSelectedTab.value);
    } else {
      navigate(
        isParticipantEnabled
          ? PATHS.STUDY_INSIGHTS_PAGES.PARTICIPANT_RECRUITMENT
          : PATHS.STUDY_INSIGHTS_PAGES.COLLECTION_AND_SEQUENCING,
      );
    }
  }, [location]);

  return (
    <TabContextProvider value={selectedTab}>
      <PageLarge
        withTooltip={titleAndTooltip.withTooltip}
        header={
          <Box
            css={{
              boxShadow: '$tabGroup',
              marginTop: '$4',
            }}
          >
            <TabGroup
              css={{
                borderBottom: 'none',
                margin: '$1 auto',
                maxWidth: '$studyInsights$containerMaxWidth',
                padding: '0 $6',
              }}
              onChange={(_, value) => {
                document.documentElement.scrollTo({
                  top: 0,
                  left: 0,
                });
                setSelectedTab(value);
              }}
              value={selectedTab}
              tabSize={Sizes.LARGE}
            >
              {tabsList.map((tab) => (
                <Tab key={tab.value} label={tab.label} value={tab.value} url={tab.url} />
              ))}
            </TabGroup>
          </Box>
        }
        title={titleAndTooltip.title}
        actions={titleAndTooltip.actions}
      >
        <>
          <Outlet />
        </>
      </PageLarge>
    </TabContextProvider>
  );
};

export default StudyInsightsScreen;
